import { useState, useEffect } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { forgotPassword } from '../core/_requests'

const initialValues = {
  email: '',
}

const forgotPasswordSchema = Yup.object().shape({
  email: Yup.string()
    .email('Formato email non valido')
    .min(3, 'Minimo 3 lettere')
    .max(50, 'Massimo 50 lettere')
    .required('Email obbligatoria'),
})

export function ForgotPassword() {
  const [loading, setLoading] = useState(false)
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined)
  const [cooldown, setCooldown] = useState<number>(0)

  useEffect(() => {
    const checkCooldown = () => {
      const until = localStorage.getItem('forgot_password_cooldown_until');
      if (until) {
        const remaining = Math.ceil((parseInt(until) - Date.now()) / 1000);
        if (remaining > 0) {
          setCooldown(remaining);
        } else {
          setCooldown(0);
          localStorage.removeItem('forgot_password_cooldown_until');
        }
      }
    };

    checkCooldown();
    const interval = setInterval(checkCooldown, 1000);
    return () => clearInterval(interval);
  }, []);

  const setCooldownPeriod = () => {
    const until = Date.now() + 60 * 1000;
    localStorage.setItem('forgot_password_cooldown_until', until.toString());
    setCooldown(60);
  };

  const formik = useFormik({
    initialValues,
    validationSchema: forgotPasswordSchema,
    onSubmit: (values, { setStatus, setSubmitting }) => {
      if (cooldown > 0) {
        setSubmitting(false);
        return;
      }
      setLoading(true)
      setHasErrors(undefined)
      forgotPassword(values.email)
        .then(() => {
          setHasErrors(false)
          setSubmitting(false)
          setLoading(false)
          setCooldownPeriod();
        })
        .catch(() => {
          // Mostriamo comunque successo per evitare l'enumerazione delle email
          setHasErrors(false)
          setLoading(false)
          setSubmitting(false)
          setCooldownPeriod();
        })
    },
  })

  return (
    <form
      className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
      noValidate
      id='kt_login_password_reset_form'
      onSubmit={formik.handleSubmit}
    >
      <div className='text-center mb-10'>
        {/* begin::Title */}
        <h1 className='text-dark fw-bolder mb-3'>Hai dimenticato la password?</h1>
        {/* end::Title */}

        {/* begin::Link */}
        <div className='text-gray-500 fw-semibold fs-6'>
          Inserisci la tua email per resettare la password.
        </div>
        {/* end::Link */}
      </div>

      {hasErrors === false && (
        <div className='mb-10 bg-light-success p-8 rounded'>
          <div className='text-success'>
            Se l'indirizzo email è presente nel nostro database, riceverai a breve un link per resettare la password. Per favore controlla la tua casella di posta.
          </div>
        </div>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fw-bolder text-gray-900 fs-6'>Email</label>
        <input
          type='email'
          placeholder='Inserisci la tua email'
          autoComplete='on'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
        <button
          type='submit'
          id='kt_password_reset_submit'
          className='btn btn-primary me-4'
          disabled={formik.isSubmitting || !formik.isValid || cooldown > 0}
        >
          {!loading && cooldown === 0 && <span className='indicator-label'>Conferma</span>}
          {!loading && cooldown > 0 && <span className='indicator-label'>Riprova tra {cooldown}s</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Richiesta in corso...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>
        <Link to='/auth/login'>
          <button
            type='button'
            id='kt_login_password_reset_form_cancel_button'
            className='btn btn-light'
            disabled={formik.isSubmitting || !formik.isValid}>
            Annulla
          </button>
        </Link>
      </div>
      {/* end::Form group */}
    </form>
  )
}
